import React, { forwardRef, useImperativeHandle, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { usePassThrough } from "primereact/passthrough";
import "./SmartDataTable.css";

const SmartDataTable = forwardRef(
  (
    {
      value,
      dataKey,

      frozenValue,

      columns = [],

      selectionMode,
      selection,
      onSelectionChange,

      expandableRows = false,
      expandedRows,
      onRowToggle,
      rowExpansionTemplate,

      rowEditor = false,
      onRowEditComplete,

      leadingColumns = [],
      trailingColumns = [],

      scrollable,
      scrollHeight,
      className = "",
      pt,

      // You can pass paginatorTemplate, rowsPerPageOptions, etc. via ...tableProps
      ...tableProps
    },
    ref
  ) => {
    const dtRef = useRef(null);
    const tablePt = usePassThrough(
      { paginator: { RPPDropdown: { panel: { className: "teamassess-table-menu" } } } },
      pt,
      { mergeSections: true, mergeProps: true }
    );

    useImperativeHandle(ref, () => ({
      exportCSV: (options) => dtRef.current?.exportCSV(options),
      getTable: () => dtRef.current
    }));

    const userHasExpander = useMemo(() => columns.some((c) => c.expander), [columns]);
    const userHasRowEditor = useMemo(() => columns.some((c) => c.rowEditor), [columns]);
    const shouldRenderExpander = expandableRows && !userHasExpander;
    const shouldRenderRowEditor = rowEditor && !userHasRowEditor;

    const handleSelectionChange = (e) => {
      onSelectionChange && onSelectionChange(e.value, e);
    };

    return (
      <DataTable
        ref={dtRef}
        value={value}
        dataKey={dataKey}
        frozenValue={frozenValue}
        selectionMode={undefined}
        selection={selection}
        onSelectionChange={handleSelectionChange}
        expandedRows={expandedRows}
        onRowToggle={onRowToggle}
        rowExpansionTemplate={rowExpansionTemplate}
        onRowEditComplete={onRowEditComplete}
        scrollable={scrollable}
        scrollHeight={scrollHeight}
        {...tableProps}
        className={`teamassess-table ${className}`}
        pt={tablePt}
      >
        {leadingColumns.map((c, i) => <Column key={`lead-${i}`} {...c} />)}

        {selectionMode && (
          <Column
            selectionMode={selectionMode}
            headerStyle={{ width: "3rem" }}
          />
        )}

        {shouldRenderExpander && <Column expander style={{ width: "3rem" }} />}

        {columns.map((col, i) => {
          const { field, header, body, editor, filter, filterElement, ...rest } = col;
          return (
            <Column
              key={rest.key ?? field ?? i}
              field={field}
              header={header}
              body={body}
              editor={editor}
              filter={filter}
              filterElement={filterElement}
              {...rest}
            />
          );
        })}

        {shouldRenderRowEditor && (
          <Column rowEditor headerStyle={{ width: "8rem" }} bodyStyle={{ textAlign: "center" }} />
        )}

        {trailingColumns.map((c, i) => <Column key={`trail-${i}`} {...c} />)}
      </DataTable>
    );
  }
);

SmartDataTable.displayName = "SmartDataTable";

SmartDataTable.propTypes = {
  value: PropTypes.array.isRequired,
  dataKey: PropTypes.string,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string,
      header: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
      body: PropTypes.func,
      editor: PropTypes.func,
      filter: PropTypes.bool,
      filterElement: PropTypes.oneOfType([PropTypes.node, PropTypes.func])
    })
  ),
  selectionMode: PropTypes.oneOf(["single", "multiple"]),
  selection: PropTypes.any,
  onSelectionChange: PropTypes.func,
  expandableRows: PropTypes.bool,
  expandedRows: PropTypes.any,
  onRowToggle: PropTypes.func,
  rowExpansionTemplate: PropTypes.func,
  rowEditor: PropTypes.bool,
  onRowEditComplete: PropTypes.func,
  leadingColumns: PropTypes.array,
  trailingColumns: PropTypes.array,
  scrollable: PropTypes.bool,
  scrollHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  frozenValue: PropTypes.array
};

export default SmartDataTable;
